/**
 * Единственный источник навигации приложения.
 *
 * Отсюда рендерятся сайдбар, хлебные крошки, заголовок экрана и раздел
 * «Pages» в Cmd+K. Раньше это были четыре вручную размноженных массива
 * внутри Sidebar.tsx — при добавлении экрана его забывали в трёх из них,
 * поэтому 19 страниц дашборда вообще не имели входа из интерфейса.
 *
 * СТРУКТУРА
 * ─────────
 * admin/dispatcher — двухуровневая: 12 рабочих областей, внутри каждой
 * второй уровень. Driver и applicant — плоские списки: у них узкий
 * workflow, группировать шесть-двенадцать экранов не во что.
 * Super-admin здесь отсутствует: это отдельное приложение (admin/,
 * 52 экрана) со своей навигационной моделью, ссылка на него —
 * явный переход «Open admin console» в меню профиля.
 *
 * ПРАВИЛА
 * ───────
 * 1. Новый экран без записи здесь недоступен из интерфейса. Это
 *    сознательно: так не повторится история с 19 потерянными.
 * 2. `hidden: true` — экран доступен по прямой ссылке и находится через
 *    Cmd+K, но не занимает место в сайдбаре (мастера, детальные
 *    экраны, вкладки других разделов).
 * 3. Права здесь НЕ проверяются — только видимость. Реальные гейты
 *    остаются в dashboard/layout.tsx и на бэкенде.
 */
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard, ClipboardList, Truck, Users, ShieldCheck, Wallet,
  FileText, BarChart3, Bot, Store, MessageSquare, Settings,
  Route, Map, ListChecks, Fuel, Boxes, Wrench, Brain, Clock, Siren,
  Calendar, Receipt, Calculator, Sparkles, Send, Building2, Plug,
  Search, Package, Warehouse, Briefcase, UserPlus, Trophy, UsersRound,
  Gauge, Leaf, CreditCard, Tag, ScrollText, Video, LifeBuoy,
} from 'lucide-react';

export type Role = 'admin' | 'driver' | 'applicant';

/** Ключ платного модуля организации — гейт из useModules(). */
export type ModuleKey =
  | 'orders' | 'routes' | 'tachograph' | 'inventory' | 'fleet'
  | 'marketing' | 'ai' | 'contacts' | 'tour-planner';

export interface NavLeaf {
  href: string;
  /** Ключ i18n. Подпись берётся из t(labelKey); фолбэк — fallback. */
  labelKey: string;
  fallback: string;
  icon?: LucideIcon;
  module?: ModuleKey;
  /** Не показывать в сайдбаре, но оставить в Cmd+K и крошках. */
  hidden?: boolean;
  /** Синонимы для поиска в командной палитре. */
  keywords?: string[];
}

export interface NavArea {
  id: string;
  labelKey: string;
  fallback: string;
  icon: LucideIcon;
  /** Экран самой области. Если задан — клик по группе ведёт сюда. */
  href?: string;
  items: NavLeaf[];
}

/* ══ ADMIN / DISPATCHER — 12 рабочих областей ══════════════════════ */

export const ADMIN_AREAS: NavArea[] = [
  {
    id: 'dashboard',
    labelKey: 'nav_dashboard', fallback: 'Dashboard', icon: LayoutDashboard,
    href: '/dashboard',
    items: [],
  },
  {
    id: 'operations',
    labelKey: 'nav_area_operations', fallback: 'Operations', icon: ClipboardList,
    items: [
      { href: '/dashboard/orders', labelKey: 'nav_orders', fallback: 'Orders', icon: ClipboardList, module: 'orders', keywords: ['заказы', 'aufträge', 'shipments'] },
      { href: '/dashboard/orders/templates', labelKey: 'nav_order_templates', fallback: 'Order templates', icon: ScrollText, hidden: true },
      { href: '/dashboard/map', labelKey: 'nav_map', fallback: 'Live map', icon: Map, keywords: ['карта', 'gps', 'tracking'] },
      { href: '/dashboard/routes', labelKey: 'nav_routes', fallback: 'Routes', icon: Route, module: 'routes' },
      { href: '/dashboard/tour-planner', labelKey: 'nav_tour_planner', fallback: 'Tour planner', icon: Map, module: 'tour-planner', keywords: ['планировщик', 'построение маршрута'] },
      { href: '/dashboard/route-search', labelKey: 'nav_smart_load', fallback: 'Smart load', icon: Search, keywords: ['догруз', 'backload'] },
      { href: '/dashboard/autopilot', labelKey: 'nav_ai_autopilot', fallback: 'Autopilot', icon: Bot, keywords: ['ai', 'автопилот'] },
      { href: '/dashboard/work-plan', labelKey: 'nav_work_plan', fallback: 'Work plan', icon: ListChecks },
      { href: '/dashboard/handover-pool', labelKey: 'nav_handover_pool', fallback: 'Handover pool', icon: UsersRound, hidden: true },
    ],
  },
  {
    id: 'fleet',
    labelKey: 'nav_area_fleet', fallback: 'Fleet', icon: Truck,
    items: [
      { href: '/dashboard/fleet', labelKey: 'nav_vehicles', fallback: 'Vehicles', icon: Truck, module: 'fleet', keywords: ['транспорт', 'машины', 'fahrzeuge'] },
      { href: '/dashboard/fleet/timeline', labelKey: 'nav_maint_timeline', fallback: 'Maintenance', icon: Wrench, module: 'fleet', keywords: ['то', 'сервис', 'service', 'журнал'] },
      { href: '/dashboard/fleet/maintenance', labelKey: 'nav_ai_maint', fallback: 'Predictive maintenance', icon: Brain, module: 'fleet', keywords: ['прогноз', 'health'] },
      // Второй прогнозный экран: предсказание по интервалам пробега.
      // TODO(fleet): свести с /fleet/maintenance в один экран с двумя
      // источниками (интервалы + AI-находки) при переносе Fleet-волны.
      { href: '/dashboard/maintenance', labelKey: 'nav_maint_intervals', fallback: 'Interval forecast', icon: Gauge, hidden: true },
      { href: '/dashboard/fuel', labelKey: 'nav_fuel', fallback: 'Fuel', icon: Fuel, keywords: ['топливо', 'заправки'] },
      { href: '/dashboard/fuel/refuel', labelKey: 'nav_refuel', fallback: 'Refuel advisor', icon: Fuel, hidden: true },
      { href: '/dashboard/inventory', labelKey: 'nav_inventory', fallback: 'Inventory', icon: Boxes, module: 'inventory' },
    ],
  },
  {
    id: 'drivers',
    labelKey: 'nav_area_drivers', fallback: 'Drivers', icon: Users,
    items: [
      { href: '/dashboard/drivers', labelKey: 'nav_drivers', fallback: 'Drivers', icon: Users, keywords: ['водители', 'fahrer'] },
      { href: '/dashboard/drivers/leaderboard', labelKey: 'nav_leaderboard', fallback: 'Leaderboard', icon: Trophy, hidden: true },
      { href: '/dashboard/leave', labelKey: 'nav_leave', fallback: 'Leave & requests', icon: Calendar, keywords: ['отпуск', 'urlaub'] },
      { href: '/dashboard/codriver', labelKey: 'nav_codriver', fallback: 'Co-driver', icon: UsersRound, hidden: true },
      { href: '/dashboard/driver-onboarding', labelKey: 'nav_onboarding', fallback: 'Driver onboarding', icon: UserPlus, hidden: true },
    ],
  },
  {
    id: 'compliance',
    labelKey: 'nav_area_compliance', fallback: 'Compliance', icon: ShieldCheck,
    items: [
      { href: '/dashboard/tachograph', labelKey: 'nav_tacho', fallback: 'Tachograph', icon: Clock, module: 'tachograph', keywords: ['eu561', 'тахограф', 'lenkzeit'] },
      { href: '/dashboard/safety', labelKey: 'saf_nav', fallback: 'Safety', icon: ShieldCheck, keywords: ['стиль вождения'] },
      { href: '/dashboard/incidents', labelKey: 'inc_nav', fallback: 'Incidents', icon: Siren, keywords: ['sos', 'дтп'] },
      { href: '/dashboard/compliance', labelKey: 'nav_compliance', fallback: 'Compliance overview', icon: ShieldCheck, hidden: true },
      { href: '/dashboard/esg', labelKey: 'nav_esg', fallback: 'ESG report', icon: Leaf, hidden: true },
    ],
  },
  {
    id: 'finance',
    labelKey: 'nav_area_finance', fallback: 'Finance', icon: Wallet,
    items: [
      { href: '/dashboard/buchhaltung', labelKey: 'nav_buchhaltung', fallback: 'Accounting', icon: Calculator },
      { href: '/dashboard/invoices', labelKey: 'nav_invoices', fallback: 'Invoices', icon: Receipt, keywords: ['счета', 'rechnungen'] },
      { href: '/dashboard/supplier-invoices', labelKey: 'nav_supplier_invoices', fallback: 'Supplier invoices', icon: Receipt },
      { href: '/dashboard/salary', labelKey: 'nav_payroll', fallback: 'Payroll', icon: Wallet, keywords: ['зарплата', 'lohn'] },
      { href: '/dashboard/payroll-history', labelKey: 'nav_payroll_history', fallback: 'Payroll history', icon: Wallet, hidden: true },
      { href: '/dashboard/payslips', labelKey: 'nav_payslips', fallback: 'Payslips', icon: FileText, hidden: true },
      { href: '/dashboard/payouts', labelKey: 'nav_payouts', fallback: 'Payouts', icon: CreditCard, hidden: true },
      // Путь называется route-planner, но экран считает стоимость рейса.
      // Подпись отражает назначение; URL не трогаем, чтобы не ломать ссылки.
      { href: '/dashboard/route-planner', labelKey: 'nav_cost_profit', fallback: 'Cost & profit', icon: Calculator, keywords: ['калькулятор', 'maut', 'toll', 'profit', 'рентабельность'] },
    ],
  },
  {
    id: 'documents',
    labelKey: 'nav_area_documents', fallback: 'Documents', icon: FileText,
    items: [
      { href: '/dashboard/documents', labelKey: 'nav_documents', fallback: 'Documents', icon: FileText, keywords: ['cmr', 'документы'] },
      { href: '/dashboard/import', labelKey: 'imp_title', fallback: 'AI import', icon: Sparkles, keywords: ['excel', 'csv', 'импорт'] },
      { href: '/dashboard/video-storage', labelKey: 'nav_video_storage', fallback: 'Video storage', icon: Video },
    ],
  },
  {
    id: 'analytics',
    labelKey: 'nav_area_analytics', fallback: 'Analytics', icon: BarChart3,
    items: [
      { href: '/dashboard/analytics', labelKey: 'analytics_pro_title', fallback: 'Analytics', icon: BarChart3 },
      { href: '/dashboard/reports', labelKey: 'nav_reports', fallback: 'Reports', icon: FileText, keywords: ['отчёты', 'выгрузка'] },
    ],
  },
  {
    id: 'ai',
    labelKey: 'nav_area_ai', fallback: 'AI', icon: Bot,
    items: [
      { href: '/dashboard/ai', labelKey: 'nav_ai', fallback: 'Assistant', icon: Bot, module: 'ai' },
      { href: '/dashboard/ai-usage', labelKey: 'nav_ai_usage', fallback: 'Usage & cost', icon: Sparkles },
    ],
  },
  {
    id: 'marketplace',
    labelKey: 'nav_area_marketplace', fallback: 'Marketplace', icon: Store,
    items: [
      { href: '/dashboard/marketplace-orders', labelKey: 'nav_customer_market', fallback: 'Incoming orders', icon: Package },
      { href: '/dashboard/marketplace', labelKey: 'nav_carriers', fallback: 'Carriers', icon: Truck, hidden: true },
      { href: '/dashboard/storage', labelKey: 'nav_storage', fallback: 'Storage', icon: Warehouse },
      { href: '/dashboard/jobs', labelKey: 'nav_jobs_market', fallback: 'Jobs', icon: Briefcase },
    ],
  },
  {
    id: 'messages',
    labelKey: 'nav_area_messages', fallback: 'Messages', icon: MessageSquare,
    items: [
      { href: '/dashboard/messages', labelKey: 'nav_messages', fallback: 'Inbox', icon: MessageSquare },
      { href: '/dashboard/chat', labelKey: 'nav_chat', fallback: 'Chat', icon: MessageSquare },
    ],
  },
  {
    id: 'settings',
    labelKey: 'nav_area_settings', fallback: 'Settings', icon: Settings,
    items: [
      { href: '/dashboard/settings', labelKey: 'nav_settings', fallback: 'Preferences', icon: Settings },
      { href: '/dashboard/settings/security', labelKey: 'nav_security', fallback: 'Security', icon: ShieldCheck },
      { href: '/dashboard/organization', labelKey: 'nav_organization', fallback: 'Company', icon: Building2 },
      { href: '/dashboard/billing', labelKey: 'nav_billing', fallback: 'Subscription', icon: CreditCard, hidden: true },
      { href: '/dashboard/contacts', labelKey: 'nav_contacts', fallback: 'Contacts', icon: Users, module: 'contacts' },
      { href: '/dashboard/marketing', labelKey: 'nav_marketing', fallback: 'Campaigns', icon: Send, module: 'marketing' },
      { href: '/dashboard/integrations/webhooks/dlq', labelKey: 'nav_webhooks', fallback: 'Webhooks', icon: Plug, hidden: true },
      { href: '/dashboard/disputes/rules', labelKey: 'nav_dispute_rules', fallback: 'Dispute rules', icon: ShieldCheck, hidden: true },
      { href: '/dashboard/complaint-tags', labelKey: 'nav_complaint_tags', fallback: 'Complaint tags', icon: Tag, hidden: true },
    ],
  },
];

/* ══ DRIVER — плоский список, узкий workflow ═══════════════════════ */

export const DRIVER_ITEMS: NavLeaf[] = [
  { href: '/dashboard', labelKey: 'nav_dashboard', fallback: 'Dashboard', icon: LayoutDashboard },
  { href: '/dashboard/work-plan', labelKey: 'nav_work_plan', fallback: 'Work plan', icon: ListChecks },
  { href: '/dashboard/orders', labelKey: 'nav_orders', fallback: 'Orders', icon: ClipboardList },
  { href: '/dashboard/fuel', labelKey: 'nav_fuel', fallback: 'Fuel', icon: Fuel },
  { href: '/dashboard/fuel/refuel', labelKey: 'nav_refuel', fallback: 'Refuel advisor', icon: Fuel },
  { href: '/dashboard/safety', labelKey: 'saf_nav', fallback: 'Safety', icon: ShieldCheck },
  { href: '/dashboard/incidents', labelKey: 'inc_nav', fallback: 'Incidents', icon: Siren },
  { href: '/dashboard/codriver', labelKey: 'nav_codriver', fallback: 'Co-driver', icon: UsersRound },
  { href: '/dashboard/leave', labelKey: 'nav_leave', fallback: 'Leave', icon: Calendar },
  { href: '/dashboard/payslips', labelKey: 'nav_payroll', fallback: 'Payslips', icon: Wallet },
  { href: '/dashboard/documents', labelKey: 'nav_documents', fallback: 'Documents', icon: FileText },
  { href: '/dashboard/chat', labelKey: 'nav_chat', fallback: 'Chat', icon: MessageSquare },
  { href: '/dashboard/job-seeker', labelKey: 'nav_job_search', fallback: 'Job search', icon: Search },
  { href: '/dashboard/driver-onboarding', labelKey: 'nav_onboarding', fallback: 'Onboarding', icon: UserPlus, hidden: true },
  { href: '/dashboard/settings', labelKey: 'nav_settings', fallback: 'Settings', icon: Settings },
];

/* ══ APPLICANT — водитель без организации ══════════════════════════ */

export const APPLICANT_ITEMS: NavLeaf[] = [
  { href: '/dashboard/seeker', labelKey: 'nav_marketplace', fallback: 'Job board', icon: Briefcase },
  { href: '/dashboard/jobs', labelKey: 'nav_jobs_search', fallback: 'Find jobs', icon: Search },
  { href: '/dashboard/payouts', labelKey: 'nav_my_account', fallback: 'My account', icon: CreditCard },
  { href: '/dashboard/chat', labelKey: 'nav_chat', fallback: 'Chat', icon: MessageSquare },
  { href: '/dashboard/documents', labelKey: 'nav_documents', fallback: 'Documents', icon: FileText },
  { href: '/dashboard/seeker-onboarding', labelKey: 'nav_onboarding', fallback: 'Onboarding', icon: UserPlus, hidden: true },
  { href: '/dashboard/settings', labelKey: 'nav_settings', fallback: 'Settings', icon: Settings },
];

/* ══ Экраны вне навигации ══════════════════════════════════════════
   Мастера на весь экран: сайдбара нет, но крошки и Cmd+K о них знают. */
export const STANDALONE: NavLeaf[] = [
  { href: '/dashboard/onboarding', labelKey: 'nav_company_setup', fallback: 'Company setup', icon: Building2, hidden: true },
];

/* ══ Производные ═══════════════════════════════════════════════════ */

/** Плоский список всех экранов роли — для Cmd+K и разрешения крошек. */
export function flattenNav(role: Role): NavLeaf[] {
  if (role === 'driver') return [...DRIVER_ITEMS, ...STANDALONE];
  if (role === 'applicant') return [...APPLICANT_ITEMS, ...STANDALONE];
  const out: NavLeaf[] = [];
  for (const area of ADMIN_AREAS) {
    if (area.href) out.push({ href: area.href, labelKey: area.labelKey, fallback: area.fallback, icon: area.icon });
    out.push(...area.items);
  }
  return [...out, ...STANDALONE];
}

/** Область, которой принадлежит путь. Самое длинное совпадение выигрывает,
 *  иначе /dashboard/fleet перехватывал бы /dashboard/fleet/timeline. */
export function areaForPath(pathname: string): NavArea | null {
  let best: NavArea | null = null;
  let bestLen = -1;
  for (const area of ADMIN_AREAS) {
    const hrefs = [...(area.href ? [area.href] : []), ...area.items.map((i) => i.href)];
    for (const href of hrefs) {
      const exact = pathname === href;
      const nested = pathname.startsWith(href + '/');
      if ((exact || nested) && href.length > bestLen) { best = area; bestLen = href.length; }
    }
  }
  return best;
}

/** Лист, соответствующий пути (для заголовка экрана и крошек). */
export function leafForPath(pathname: string, role: Role = 'admin'): NavLeaf | null {
  const all = flattenNav(role);
  let best: NavLeaf | null = null;
  for (const leaf of all) {
    if (pathname === leaf.href) return leaf;
    if (pathname.startsWith(leaf.href + '/') && (!best || leaf.href.length > best.href.length)) best = leaf;
  }
  return best;
}
